import type { Opportunity } from "./types";

const baseUrl = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 8_000);
  try {
    const response = await fetch(`${baseUrl}${path}`, {
      ...init,
      signal: controller.signal,
      headers: { Accept: "application/json", ...(init?.body ? { "Content-Type": "application/json" } : {}), ...init?.headers },
    });
    if (!response.ok) throw new Error(`API request failed: ${response.status}`);
    return (await response.json()) as T;
  } finally {
    window.clearTimeout(timeout);
  }
}

export async function loadOpportunities() {
  const response = await request<{ items: Opportunity[] }>("/api/opportunities?limit=100");
  return response.items;
}

export function submitOpportunity(payload: Record<string, unknown>) {
  return request<{ id: string; status: "pending" }>("/api/opportunities", { method: "POST", body: JSON.stringify(payload) });
}

export function submitInquiry(payload: Record<string, unknown>) {
  return request<{ id: string; status: "pending" }>("/api/inquiries", { method: "POST", body: JSON.stringify(payload) });
}

const countryCenters: Record<string, [number, number]> = {
  AT: [16.3738, 48.2082], HU: [19.0402, 47.4979], DE: [11.582, 48.1351], SK: [17.1077, 48.1486],
  CZ: [16.6068, 49.1951], PL: [19.945, 50.0647], RO: [26.1025, 44.4268], SI: [14.5058, 46.0569], HR: [15.9819, 45.815],
};

export async function geocode(city: string, country: string): Promise<[number, number]> {
  const fallback = countryCenters[country] ?? [16.3738, 48.2082];
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 4_000);
  try {
    const url = new URL("https://photon.komoot.io/api/");
    url.searchParams.set("q", `${city}, ${country}`);
    url.searchParams.set("limit", "1");
    const response = await fetch(url, { signal: controller.signal });
    if (!response.ok) return fallback;
    const data = (await response.json()) as { features?: Array<{ geometry?: { coordinates?: [number, number] } }> };
    const coordinates = data.features?.[0]?.geometry?.coordinates;
    return coordinates && coordinates.every(Number.isFinite) ? coordinates : fallback;
  } catch {
    return fallback;
  } finally {
    window.clearTimeout(timeout);
  }
}
